// import { ReactRenderer } from "@tiptap/react";
// import { SuggestionOptions } from "@tiptap/suggestion";
// import tippy, { GetReferenceClientRect, Instance, Props } from "tippy.js";
// import MentionList from "./mention-list";
// import "./style.css";
// export interface SuggestionProps {
//   type?: "user" | "item";
//   heading: string;
//   children: SuggestionItem[];
// }
// export interface SuggestionItem {
//   label: string;
//   email?: string;
//   url?: string;
//   role?: string;
//   description?: string;
//   reference?: string;
//   image?: string;
//   id?: string;
//   modelType?:
//     | "Incident"
//     | "Observation"
//     | "Inspection"
//     | "Audit"
//     | "Certification"
//     | "Regulation";
//   type?: "user" | "item";
// }
// const createSuggestion = (
//   trigger: string,
//   items: SuggestionProps[],
// ): Partial<SuggestionOptions> => {
//   console.log(trigger, items, "trigger");
//   return {
//     char: trigger,
//     items: async ({ query }) => {
//       console.log(items, "items in the return");
//       return items.filter((item) =>
//         item.children.some((child) =>
//           child.label.toLowerCase().includes(query.toLowerCase()),
//         ),
//       );
//     },
//     render: () => {
//       let reactRenderer: ReactRenderer | null = null;
//       let popup: Instance<Props> | null = null;
//       return {
//         onStart: (props) => {
//           reactRenderer = new ReactRenderer(MentionList, {
//             props,
//             editor: props.editor,
//           });
//           popup = tippy(props.editor.view.dom as Element, {
//             getReferenceClientRect: props.clientRect as GetReferenceClientRect,
//             appendTo: () => document.body, // Render outside modal to allow scrolling
//             content: reactRenderer.element,
//             showOnCreate: true,
//             interactive: true,
//             arrow: false,
//             trigger: "manual",
//             placement: "bottom",
//             interactiveDebounce: 0,
//             popperOptions: {
//               modifiers: [
//                 {
//                   name: "preventOverflow",
//                   options: {
//                     boundary: "viewport",
//                   },
//                 },
//                 {
//                   name: "flip",
//                   options: {
//                     fallbackPlacements: ["top", "bottom", "right", "left"],
//                   },
//                 },
//               ],
//             },
//           });
//         },
//         onUpdate: (props) => {
//           if (reactRenderer) {
//             reactRenderer.updateProps(props);
//           }
//           if (popup) {
//             popup.setProps({
//               getReferenceClientRect:
//                 props.clientRect as GetReferenceClientRect,
//             });
//           }
//         },
//         onExit: () => {
//           if (popup) {
//             popup.destroy();
//             popup = null;
//           }
//           if (reactRenderer) {
//             reactRenderer.destroy();
//             reactRenderer = null;
//           }
//         },
//       };
//     },
//     command: ({ editor, range, props }) => {
//       editor
//         .chain()
//         .focus()
//         .insertContentAt(range, [
//           {
//             type: "mention",
//             attrs: { id: props.id, label: props.label, href: props.id },
//           },
//           { type: "text", text: " " },
//         ])
//         .run();
//     },
//     allowSpaces: true,
//   };
// };
// export default createSuggestion;
import { ReactRenderer } from "@tiptap/react";
import { SuggestionOptions } from "@tiptap/suggestion";
import { RefObject } from "react";
import tippy, { GetReferenceClientRect, Instance, Props } from "tippy.js";

import { filterSuggestionGroups, isQueryAbandoned } from "./filter";
import MentionList, { MentionListHandle } from "./mention-list";
import "./style.css";

export interface SuggestionProps {
  type?: "user" | "item";
  heading: string;
  children: SuggestionItem[];
}

export interface SuggestionItem {
  label: string;
  email?: string;
  url?: string;
  role?: string;
  description?: string;
  reference?: string;
  image?: string;
  id?: string;
  modelType?:
    | "Incident"
    | "Observation"
    | "Inspection"
    | "Audit"
    | "Certification"
    | "Regulation";
  type?: "user" | "item";
}

const createSuggestion = (
  trigger: string,
  suggestionRef: RefObject<SuggestionProps[]>,
  userId?: string,
): Partial<SuggestionOptions> => ({
  char: trigger,
  items: async ({ query }) => {
    const items = suggestionRef.current ?? [];
    // The user has stopped searching and is just writing prose after a stray `@`.
    if (isQueryAbandoned(query)) return [];
    // Filters and ranks *children*, dropping groups that end up empty.
    return filterSuggestionGroups(items, query);
  },
  render: () => {
    let reactRenderer: ReactRenderer<MentionListHandle> | null = null;
    let popup: Instance<Props> | null = null;

    return {
      onStart: (props) => {
        if (!props.clientRect) return;

        reactRenderer = new ReactRenderer(MentionList, {
          props: { ...props, query: props.query, userId },
          editor: props.editor,
        });

        popup = tippy(props.editor.view.dom as Element, {
          getReferenceClientRect: props.clientRect as GetReferenceClientRect,
          appendTo: () => document.body,
          content: reactRenderer.element,
          showOnCreate: true,
          interactive: true,
          arrow: false,
          trigger: "manual",
          placement: "bottom",
          interactiveDebounce: 0,
          popperOptions: {
            modifiers: [
              {
                name: "preventOverflow",
                options: {
                  boundary: "viewport",
                },
              },
              {
                name: "flip",
                options: {
                  fallbackPlacements: ["top", "bottom", "right", "left"],
                },
              },
            ],
          },
        });
      },
      onUpdate: (props) => {
        if (!props.clientRect) return;

        reactRenderer?.updateProps({ ...props, query: props.query, userId });

        if (popup) {
          popup.setProps({
            getReferenceClientRect: props.clientRect as GetReferenceClientRect,
          });
          // Close the popup once the query stops matching anything rather than
          // leaving an empty box hovering over the text.
          if (isQueryAbandoned(props.query)) {
            popup.hide();
          } else {
            popup.show();
          }
        }
      },
      onKeyDown: (props) => {
        // Escape always closes the popup outright — categories are toggled by
        // clicking their headers, so there is no state for Escape to unwind.
        if (props.event.key === "Escape") {
          popup?.hide();
          return true;
        }
        return reactRenderer?.ref?.onKeyDown(props) ?? false;
      },
      onExit: () => {
        popup?.destroy();
        popup = null;
        reactRenderer?.destroy();
        reactRenderer = null;
      },
    };
  },
  command: ({ editor, range, props }) => {
    editor
      .chain()
      .focus()
      .insertContentAt(range, [
        {
          type: "mention",
          attrs: { id: props.id, label: props.label, href: props.id },
        },
        { type: "text", text: " " },
      ])
      .run();
  },
  allowSpaces: true,
});

export default createSuggestion;
